import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Building2, Ticket, TrendingUp, DollarSign, 
  BarChart3, Globe, CheckCircle, Clock, ArrowUpRight,
  ArrowDownRight, Percent, Activity
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SuperAdminDashboard Component
 * Displays comprehensive KPIs for the platform super admin
 */
export const SuperAdminDashboard = ({ adminId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const response = await axios.get(`${API}/stats/admin/dashboard`, {
          headers: { 'X-User-Id': adminId }
        });
        setData(response.data);
      } catch (err) {
        console.error('Error fetching admin dashboard:', err);
        setError(err.response?.data?.detail || 'Error loading dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (adminId) {
      fetchDashboard();
    }
  }, [adminId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl">
        {error}
      </div>
    );
  }

  if (!data) return null;

  const { global_stats, mrr, plan_distribution, weekly_activity, tenants } = data;

  // Calculate changes (mock for now - would need historical data)
  const formatCurrency = (value) => `€${value?.toLocaleString() || 0}`;

  return (
    <div className="space-y-6" data-testid="super-admin-dashboard">
      {/* Header Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* MRR */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl p-5 text-white"
        >
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-5 h-5 opacity-80" />
            <span className="text-sm opacity-80">{t('super_admin.mrr')}</span>
          </div>
          <p className="text-3xl font-bold">{formatCurrency(mrr?.total)}</p>
          <div className="flex items-center gap-1 mt-2 text-sm text-emerald-100">
            <ArrowUpRight className="w-4 h-4" />
            <span>{t('super_admin.active')}</span>
          </div>
        </motion.div>

        {/* Total Users */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('super_admin.total_users')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {global_stats?.total_users?.toLocaleString()}
          </p>
          <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
            <ArrowUpRight className="w-4 h-4" />
            <span>+{weekly_activity?.new_users || 0} {t('super_admin.weekly_activity')}</span>
          </div>
        </motion.div>

        {/* Total Businesses */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('super_admin.total_businesses')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {global_stats?.total_businesses?.toLocaleString()}
          </p>
          <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
            <ArrowUpRight className="w-4 h-4" />
            <span>+{weekly_activity?.new_businesses || 0} {t('super_admin.weekly_activity')}</span>
          </div>
        </motion.div>

        {/* Conversion Rate */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center gap-2 mb-3">
            <Percent className="w-5 h-5 text-amber-500" />
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('super_admin.conversion_rate')}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {weekly_activity?.conversion_rate || 0}%
          </p>
          <p className="text-sm text-gray-500 mt-2">
            {weekly_activity?.completed_reservations || 0} / {weekly_activity?.reservations || 0}
          </p>
        </motion.div>
      </div>

      {/* Second Row - Plan Distribution & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-emerald-600" />
            {t('super_admin.plan_distribution')}
          </h3>
          
          <div className="space-y-4">
            {['free', 'starter', 'pro', 'business'].map((plan) => {
              const count = plan_distribution?.[plan] || 0;
              const total = Object.values(plan_distribution || {}).reduce((a, b) => a + b, 0) || 1;
              const percent = Math.round((count / total) * 100);
              
              const colors = {
                free: 'bg-gray-400',
                starter: 'bg-blue-500',
                pro: 'bg-purple-500',
                business: 'bg-amber-500'
              };
              
              return (
                <div key={plan}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize font-medium">{plan}</span>
                    <span className="text-gray-500">{count} ({percent}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors[plan]} rounded-full transition-all duration-500`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Weekly Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-emerald-600" />
            {t('super_admin.weekly_activity')}
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
              <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">{t('super_admin.new_users')}</p>
              <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                +{weekly_activity?.new_users || 0}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
              <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">{t('super_admin.new_businesses')}</p>
              <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">
                +{weekly_activity?.new_businesses || 0}
              </p>
            </div>
            <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4">
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">{t('super_admin.reservations')}</p>
              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                {weekly_activity?.reservations || 0}
              </p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
              <p className="text-sm text-amber-600 dark:text-amber-400 mb-1">{t('super_admin.completed')}</p>
              <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                {weekly_activity?.completed_reservations || 0}
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Cities Overview */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5 text-emerald-600" />
          {t('super_admin.cities_overview')}
        </h3>
        
        {tenants && tenants.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="pb-3 font-medium">{t('super_admin.city')}</th>
                  <th className="pb-3 font-medium">{t('super_admin.status')}</th>
                  <th className="pb-3 font-medium text-right">{t('super_admin.total_users')}</th>
                  <th className="pb-3 font-medium text-right">{t('super_admin.total_businesses')}</th>
                  <th className="pb-3 font-medium text-right">{t('super_admin.reservations')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {tenants.map((tenant) => (
                  <tr key={tenant.tenant_id} className="text-sm">
                    <td className="py-3 font-medium text-gray-900 dark:text-white">
                      {tenant.name}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        tenant.is_active 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {tenant.is_active ? (
                          <><CheckCircle className="w-3 h-3" /> {t('super_admin.active')}</>
                        ) : (
                          <><Clock className="w-3 h-3" /> {t('super_admin.inactive')}</>
                        )}
                      </span>
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-300">
                      {tenant.total_users?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-300">
                      {tenant.total_businesses?.toLocaleString() || 0}
                    </td>
                    <td className="py-3 text-right text-gray-600 dark:text-gray-300">
                      {tenant.total_reservations?.toLocaleString() || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>{t('tenants.title')}</p>
          </div>
        )}
      </motion.div>

      {/* Points Redemption Progress */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-gradient-to-r from-amber-50 to-emerald-50 dark:from-amber-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800"
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg">{t('user_counter.redemption_title')}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {global_stats?.points_redemption?.is_active 
                ? t('user_counter.redemption_active')
                : t('user_counter.redemption_progress', { users: global_stats?.points_redemption?.users_until_active?.toLocaleString() })
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-600">
              {global_stats?.points_redemption?.progress_percent}%
            </p>
          </div>
        </div>
        
        <div className="h-4 bg-white dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 rounded-full transition-all duration-1000"
            style={{ width: `${global_stats?.points_redemption?.progress_percent || 0}%` }}
          />
        </div>
        
        <div className="flex justify-between mt-2 text-sm text-gray-500">
          <span>{global_stats?.total_users?.toLocaleString()} {t('user_counter.users')}</span>
          <span>{global_stats?.points_redemption?.threshold?.toLocaleString()} {t('user_counter.goal')}</span>
        </div>
      </motion.div>
    </div>
  );
};

export default SuperAdminDashboard;
